/**
 * Helper: Cart
 * ------------------------------------------------------------------------------
 * - Global helper to interact with the Shopify Cart.
 * - IMPORTANT: This helper will be phased out in favour of the Pinia-based
 *   cart, found in mint/stores/cart.js
 * - If using a Vue component, use that instead!
 * - https://shopify.dev/api/ajax/reference/cart
 *
 * @namespace Cart
 */
import { removeItem } from '@shopify/theme-cart'

import { changeItem, fetchCart } from '~/api/cart'

/**
 * Selectors.
 */
const selectors = {
  cartCountIcon: '[data-cart-count]',
}

/**
 * Create a new Cart JS object.
 */
export default () => {

  /**
   * Node Selectors.
   */
  const nodeSelectors = {
    cartCountIcon: [...document.querySelectorAll(selectors.cartCountIcon)],
  }

  /**
   * Default config for a given API call.
   * - Allows for Shopify endpoints to correctly return error states.
   */
  const defaultConfig = {
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
  }

  /**
   * State.
   */
  const state = {
    cart: {},
  }

  /**
   * Initialise component.
   */
  function init() {
    setEventBusListeners()
  }

  /**
   * Set Event Bus Listeners
   */
  function setEventBusListeners() {

    /**
     * Listen for a request to add items to the cart.
     */
    mint.EventBus.on('cart:addItems', (request) => {
      handleAddItemsToCart(request)
    })

    /**
     * Listen for a request to remove an item from the cart.
     * @param {String} key - Cart.js line item key.
     */
    mint.EventBus.on('cart:removeItem', (key) => {
      handleRemoveItemFromCart(key)
    })

    /**
     * Listen for a request to change a lin item in the cart.
     */
    mint.EventBus.on('cart:changeItem', (request) => {
      handleChangeCartItem(request)
    })

    /**
     * A given item has successfully been added to the cart.
     * - Trigger events to fetch the cart and broadcast within the EventBus.
     * - Define Array first, as compiler doesn't support [...].forEach
     */
    const events = [
      'cart:added',
      'cart:addFailed',
      'cart:changed',
      'cart:fetch',
      'cart:removed',
    ]
    events.forEach((event) => {
      mint.EventBus.on(event, (response) => {
        handleCartUpdate(event, response)
      })
    })

    /**
     * All updates to the cart have been completed.
     * - This listener can be added to other components for interoperability.
     */
    mint.EventBus.on('mint:cart:updated', (response) => {
      state.cart = response
      updateCartIconCounter(response)
    })
  }

  /**
   * Update all cart icon counters.
   * @param {Object} cart - Cart object from Shopify.
   */
  function updateCartIconCounter(cart) {
    const total = cart.item_count

    nodeSelectors.cartCountIcon.forEach((icon) => {
      const classAction = total === 0 ? 'add' : 'remove'

      icon.innerText = total
      icon.classList[classAction]('hide')
    })
  }

  /**
   * Add a given item to the cart and emit an Event.
   * @param {Array} request - Array of items of which to add to the cart.
   * @param {Array} request.body - Array of items of which to add to the cart.
   * @param {Object} request[key] - Any additional parameter data to pass in.
   */
  async function handleAddItemsToCart(request) {
    let event = 'cart:added'
    let response = false

    try {
      response = await addItems(request.data)

      /**
       * Add to cart has failed.
       * - Shopify 200'd the request, but rejected.
       * - Think Quantity errors, variant ID errors.
       * - Fetch/Network requests occur within the `catch`
       */
      if (response?.message) {
        event = 'cart:addFailed'
      }

      mint.EventBus.dispatch(event, {
        response,
        request,
      })

    } catch (error) {
      mint.Log('fatal', 'Failed to add items to the cart',
        error,
        request,
      )
    }
  }

  /**
   * Change a given line item from the cart.
   * - Data should be driven from the line item key.
   * @param {Object} request - Cart payload data.
   * @param {Object} request.data - Cart JS 'change.js' payload.
   * @param {Object} request.meta - Custom object to pass data through, useful for
   * GA, Bloomreach, Attraqt event tracking etc.
   */
  async function handleChangeCartItem(request) {
    let event = 'cart:changed'
    let response = false

    try {
      response = await changeItem(request.data)

      if (response?.message) {
        event = 'cart:changeFailed'
      }

      mint.EventBus.dispatch(event, {
        response,
        request,
      })

    } catch (error) {
      mint.Log('fatal', 'Failed to change item in the cart',
        error,
        request,
      )
    }
  }

  /**
   * Remove a given item from the cart and emit an Event.
   * @param {String} key - Unique cart line item key.
   */
  async function handleRemoveItemFromCart(key) {
    const request = {}

    try {
      const matchingLineItem = state.cart?.items?.find((item) => {
        return item.key === key
      })

      request.data = matchingLineItem || {}

      const response = await removeCartItem(key)

      mint.EventBus.dispatch('cart:removed', {
        response,
        request,
      })

    } catch (error) {
      mint.Log('fatal', 'Failed to remove item from the cart',
        error,
        {
          cartItems: state?.cart?.items || {},
          request,
          key,
        },
      )
    }
  }

  /**
   * Handle the adding of an array of items to the cart.
   * @param {String} event - Name of the event that triggered update.
   * @param {Object} [updateData] - Data from the original event - contains cart request/response.
   */
  async function handleCartUpdate(event, updateData) {
    try {
      const cart = await fetchCart()

      /**
       * Build the response object.
       * - Appends custom data
       * - First append the request body from the triggering event
       */
      const payload = {
        ...cart,
        request: {
          event,
        },
      }

      if (updateData?.request?.data) {
        payload.request.data = updateData.request.data
      }

      if (updateData?.meta) {
        payload.request.meta = updateData?.meta
      }

      /**
       * Initial request 200d, but was rejected by Shopify.
       * - i.e. quantity errors, variant ID errors.
       */
      if (updateData?.response?.message) {
        payload.request.error = updateData.response
      }

      /**
       * If the cart add request requires stopping the popup opening,
       * indicate this in the general payload
       */
      if (updateData?.request?.preventDrawer) {
        payload.request.preventDrawer = updateData.request.preventDrawer
      }

      /**
       * Notify the components.
       */
      mint.EventBus.dispatch('mint:cart:updated', payload)

    } catch (error) {
      mint.Log('fatal', 'Failed to fetch cart after update',
        error,
        {
          updateData,
        },
      )
    }
  }

  /**
   * Add an item to the cart.
   * - Accepts a single item entry or an array of items.
   * @example:
   * mint.EventBus.dispatch('cart:addItems', {
   *  data: {
   *    id: 123,
   *    quantity: 1
   *  },
   *  preventDrawer: true
   * })
   *
   * mint.EventBus.dispatch('cart:addItems', {
   *  data: [
   *   {
   *    id: 123,
   *    quantity: 1
   *  },
   *  {
   *   id: 456,
   *   quantity: 2
   *  }
   * })
   *
   * @param {Array|Object} items - Cart item(s) to add to the Cart.
   * @param {Array} items.item.id - Variant ID of the product to add.
   * @param {Array} items.item.quantity - Numerical quantity of an item to add.
   * @return {Object}
   */
  function addItems(items = []) {
    return new Promise((resolve, reject) => {
      fetch(mint.routes.cart.add, {
        ...defaultConfig,
        body: JSON.stringify(items),
      })
        .then((response) => response.json())
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  }

  /**
   * Remove an item from the cart.
   * @param {String} key - Cart line item key.
   * @return {Object}
   */
  function removeCartItem(key) {
    return new Promise((resolve, reject) => {
      removeItem(key)
        .then((response) => resolve(response))
        .catch((error) => reject(error))
    })
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  })
}
